import {
  ChangeDetectionStrategy,
  Component,
  signal,
  input,
  computed
} from "@angular/core";
import { ActionSheetController, AlertController, AlertInput, IonicModule, LoadingController, ModalController, NavController, ToastController, isPlatform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { ActivatedRoute } from "@angular/router";
import { Subject } from "rxjs";
import { DatePipe } from "@angular/common";
import { DecimalPipe } from "@angular/common";
import { selectStudentById } from "src/app/students/ngrx/students.selectors";
import { formatDate } from "src/app/utils/DateUtils";
import { selectSelectedPayment } from "../../payment-ngrx/payments.selectors";
import { deletePayment } from "../../payment-ngrx/payments.actions";
import { PaymentPdfDto } from "../../interfaces/payment_pdf_dto";
import { PdfPreviewComponent } from "../../components/pdf-preview/pdf-preview.component";
import { QrBillService } from "../../services/qr-bill.service";
import { selectCustomer } from "src/app/auth/ngrx/customer.selectors";
import { ModeService } from "src/app/utils/mode.service";

@Component({
    selector: "app-payment-detail-page",
    templateUrl: "./payment-detail-page.component.html",
    styleUrls: ["./payment-detail-page.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IonicModule,
        DatePipe,
        DecimalPipe,
    ]
})
export class PaymentDetailPageComponent {
  readonly mobile = input(true);
  isModal: boolean = false;
  originPage: string;
  paymentSignal = this.store.selectSignal(selectSelectedPayment);
  studentSignal = computed(() => this.store.selectSignal(selectStudentById(this.paymentSignal()?.student_id))());
  customerSignal = this.store.selectSignal(selectCustomer);
  loading = signal(false);
  formattedDate = computed(() => formatDate(new Date(this.paymentSignal()?.date)));
  private destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private router: ActivatedRoute,
    private modalController: ModalController,
    private actionSheetCtrl: ActionSheetController,
    private alertController: AlertController,
    private toastCtrl: ToastController,
    private qrBillService: QrBillService,
    private loadingCtrl: LoadingController,
    public modeService: ModeService,
    private navController: NavController,
  ) {}

  ngOnInit() {
    this.originPage = isPlatform("ios")
      ? this.router.snapshot.paramMap.get("originPage")
      : "";
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ionViewDidLeave() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeModal() {
    this.modalController.dismiss();
  }

  async presentPaymentActionSheet() {
    const actionSheet = await this.actionSheetCtrl.create({
      header: "Aktionen",
      buttons: this.paymentActionSheetButtons,
    });

    await actionSheet.present();
  }

  public paymentActionSheetButtons = [
    {
      text: "Zahlungsbeleg generieren",
      handler: () => {
        this.generateReceiptPreview();
      },
    },
    {
      text: "Zahlung löschen",
      role: "destructive",
      handler: () => {
        this.presentDeleteAlert();
      },
    },
    {
      text: "Abbrechen",
      role: "cancel",
      data: {
        action: "cancel",
      },
    },
  ];

  async generateReceiptPreview() {
    this.loading.set(true);
    
    const loading = await this.loadingCtrl.create({
      message: "Erstelle Quittung...",
    });

    await loading.present();
    
    try {
      const payment = this.paymentSignal();
      const student = this.studentSignal();
      const customer = this.customerSignal();
      
      if (!payment || !student || !customer) {
        throw new Error('Daten konnten nicht geladen werden');
      }
      
      const paymentPdfDto: PaymentPdfDto = {
        logo_url: customer.logo_url,
        logo_alignment: customer.logo_alignment,
        amount: payment.amount,
        payment_method: payment.payment_method,
        date: new Date(payment.date).toLocaleDateString("fr-CH"),
        currency: "CHF",
        payment_currency: "CHF",
        creditor: {
          brand: customer.brand,
          name: customer.forename + " " + customer.surname,
          address: customer.street,
          zip: customer.postal_code,
          city: customer.city,
          account: customer.iban,
          country: "CH",
        },
        debtor: {
          name: student.forename + " " + student.surname,
          address: student.street,
          zip: student.postal_code,
          city: student.city,
          country: "CH",
        }
      };
      
      try {
        const blobUrl = await this.qrBillService.generateReceipt(paymentPdfDto);
        
        loading.dismiss();
        this.loading.set(false);
        
        const modal = await this.modalController.create({
          component: PdfPreviewComponent,
          componentProps: {
            pdfSrc: signal(blobUrl),
            fileName: signal(`Quittung_${this.formattedDate()}_${student.surname}.pdf`)
          },
          cssClass: "invoice-preview-modal" + (!isPlatform("mobile") ? ' fullscreen' : ''),
        });
        
        await modal.present();
      } catch (error) {
        let errorMessage = "Quittung konnte nicht erstellt werden";
        if (error.message && error.message.includes("IBAN")) {
          errorMessage = "Die angegebene IBAN ist ungültig. Bitte überprüfe die IBAN in deinen Einstellungen und versuche es erneut.";
        }
        throw error;
      }
    } catch (error) {
      loading.dismiss();
      this.loading.set(false);
      
      const toast = await this.toastCtrl.create({
        message: "Quittung konnte nicht erstellt werden",
        duration: 3000,
        position: "bottom",
      });
      
      await toast.present();
    }
  }

  async presentDeleteAlert() {
    const alert = await this.alertController.create({
      header: "Achtung!",
      subHeader: "Bist du sicher?",
      message:
        "Diese Aktion kann nicht rückgängig gemacht werden. Zugehörige Rechnungen werden nicht gelöscht.",
      buttons: this.deleteAlertButtons,
      inputs: this.deleteAlertInputs,
    });

    alert.present().then(() => {
      /** disable the confirm button initial */
      document.querySelector('ion-alert div.alert-button-group button:nth-of-type(2)').setAttribute('disabled', 'true');
      (document.querySelector('ion-alert div.alert-button-group button:nth-of-type(2)') as HTMLElement).style.opacity = '0.5';
      return;
    });
  }

  public deleteAlertInputs = [
    {
      type: "checkbox" as "checkbox",
      label: "Ich bestätige, dass ich die Zahlung unwiderruflich löschen möchte.",
      handler: () => {
        this.handleConfirmButtonState();
      }
    } as AlertInput
  ]

  handleConfirmButtonState() {
    if (document.querySelector('ion-alert div.alert-button-group button:nth-of-type(2)').hasAttribute('disabled')) {
      document.querySelector('ion-alert div.alert-button-group button:nth-of-type(2)').removeAttribute('disabled');
      (document.querySelector('ion-alert div.alert-button-group button:nth-of-type(2)') as HTMLElement).style.opacity = '1';
    }
  }

  public deleteAlertButtons = [
    {
      text: "Abbrechen",
      role: "cancel",
      cssClass: "secondary",
    },
    {
      text: "Löschen",
      role: "destructive",
      handler: () => {
        if (this.paymentSignal() && this.paymentSignal().payment_id) {
          this.store.dispatch(deletePayment({ payment_id: this.paymentSignal().payment_id }));
          this.alertController.dismiss();
          this.navController.pop();
          this.modalController.dismiss();
        }
      },
    },
  ];
} 